<template>
  <div class="login-page">
    <div class="login-shell">
      <div class="login-header">
        <p class="eyebrow">Welcome Back</p>
        <h1>Login to Your Account</h1>
        <p>Enter your phone number and verify the code we send you.</p>
      </div>

      <form v-if="step === 'phone'" class="login-card" @submit.prevent="sendLoginCode">
        <div class="form-group">
          <label for="phone" class="form-label">Phone Number</label>
          <div class="phone-input-wrap" :class="{ error: errors.phone }">
            <span class="country-prefix" aria-hidden="true">+1</span>
            <input
              id="phone"
              v-model="form.phone"
              type="tel"
              inputmode="numeric"
              autocomplete="tel-national"
              class="phone-input"
              placeholder="555 123 4567"
              maxlength="14"
              required
              aria-describedby="phone-help"
              @input="formatPhoneInput"
            />
          </div>
          <p id="phone-help" class="form-help">Paulie is currently available only in the U.S. and Canada.</p>
          <span v-if="errors.phone" class="error-message">{{ errors.phone }}</span>
        </div>

        <button type="submit" class="btn btn-primary w-full" :disabled="loading">
          <span v-if="loading" class="spinner"></span>
          {{ loading ? 'Sending Code...' : 'Send Code' }}
        </button>

        <p class="login-footer">
          Need an account?
          <router-link class="link" :to="{ path: '/register', query: phoneQuery }">Register</router-link>
        </p>
      </form>

      <form v-else class="login-card" @submit.prevent="verifyLoginCode">
        <div class="code-header">
          <p class="eyebrow">Verify Phone</p>
          <h2>Enter The Code</h2>
          <p>We sent a one-time code to {{ maskedPhone }}.</p>
        </div>

        <div class="form-group">
          <label for="code" class="form-label">Verification Code</label>
          <input
            id="code"
            v-model="form.code"
            type="text"
            inputmode="numeric"
            autocomplete="one-time-code"
            class="form-input code-input"
            :class="{ error: errors.code }"
            maxlength="8"
            placeholder="123456"
            required
          />
          <span v-if="errors.code" class="error-message">{{ errors.code }}</span>
        </div>

        <div class="login-actions">
          <button type="submit" class="btn btn-primary" :disabled="loading">
            <span v-if="loading" class="spinner"></span>
            {{ loading ? 'Verifying...' : 'Login' }}
          </button>
          <button type="button" class="btn btn-outline" :disabled="loading" @click="step = 'phone'">
            Back
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import { computed, reactive, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { auth, hasSupabaseEnv, userProfiles } from '@/lib/supabase'

const route = useRoute()
const router = useRouter()

const loading = ref(false)
const step = ref('phone')
const errors = reactive({})

const form = reactive({
  phone: formatPhoneFromQuery(route.query.phone),
  code: ''
})

const phoneDigits = computed(() => form.phone.replace(/\D/g, '').slice(0, 10))
const normalizedPhone = computed(() => (phoneDigits.value.length === 10 ? `+1${phoneDigits.value}` : ''))
const phoneQuery = computed(() => (normalizedPhone.value ? { phone: normalizedPhone.value } : {}))
const maskedPhone = computed(() => {
  const value = normalizedPhone.value
  if (value.length < 5) return value
  return `${value.slice(0, 3)}${'*'.repeat(Math.max(value.length - 5, 3))}${value.slice(-2)}`
})

function formatPhoneFromQuery(value) {
  const digits = String(value || '').replace(/\D/g, '').replace(/^1/, '').slice(0, 10)

  if (digits.length <= 3) return digits
  if (digits.length <= 6) return `${digits.slice(0, 3)} ${digits.slice(3)}`
  return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`
}

const formatPhoneInput = () => {
  form.phone = formatPhoneFromQuery(form.phone)
  errors.phone = ''
}

const validatePhone = () => {
  errors.phone = ''

  if (!phoneDigits.value) {
    errors.phone = 'Phone number is required.'
  } else if (phoneDigits.value.length !== 10) {
    errors.phone = 'Enter a 10-digit U.S. or Canada phone number.'
  } else if (!/^[2-9]\d{2}[2-9]\d{6}$/.test(phoneDigits.value)) {
    errors.phone = 'Enter a valid U.S. or Canada phone number.'
  }

  return !errors.phone
}

const validateCode = () => {
  errors.code = ''

  if (!form.code.trim()) {
    errors.code = 'Verification code is required.'
  } else if (!/^\d{4,8}$/.test(form.code.trim())) {
    errors.code = 'Enter the numeric code from your SMS.'
  }

  return !errors.code
}

const sendLoginCode = async () => {
  if (!validatePhone()) return

  if (!hasSupabaseEnv) {
    errors.phone = 'Supabase is not configured for this local app. Add VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY, then restart the dev server.'
    return
  }

  loading.value = true

  try {
    const { error } = await auth.sendPhoneOtp(normalizedPhone.value)

    if (error) {
      throw error
    }

    step.value = 'code'
  } catch (error) {
    console.error('Phone login error:', error)
    errors.phone = formatAuthError(error, 'We could not send a code. Register first if this phone is new.')
  } finally {
    loading.value = false
  }
}

const verifyLoginCode = async () => {
  if (!validateCode()) return

  loading.value = true

  try {
    const { data, error } = await auth.verifyPhoneOtp(normalizedPhone.value, form.code.trim())

    if (error) {
      throw error
    }

    const currentUser = data?.user
    const { data: profile, error: profileError } = await userProfiles.touchLogin(currentUser?.id)

    if (profileError) {
      throw profileError
    }

    if (!profile?.username && currentUser?.user_metadata?.username) {
      const { error: backfillError } = await userProfiles.complete({
        userId: currentUser.id,
        phoneNumber: currentUser.phone || normalizedPhone.value,
        username: currentUser.user_metadata.username,
        joinCode: currentUser.user_metadata.join_code || ''
      })

      if (backfillError) {
        throw backfillError
      }
    }

    router.push('/pro-dashboard')
  } catch (error) {
    console.error('Phone login verify error:', error)
    errors.code = formatAuthError(error, 'That code did not work. Check the SMS and try again.')
  } finally {
    loading.value = false
  }
}

const formatAuthError = (error, fallback) => {
  const message = error?.message || ''

  if (!message) return fallback
  return `${fallback} Supabase returned: ${message}`
}
</script>

<style scoped>
.login-page {
  min-height: calc(100vh - 220px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: clamp(1rem, 3vw, 2.5rem) 1rem 3rem;
}

.login-shell {
  width: min(420px, 100%);
}

.login-header {
  text-align: center;
  margin-bottom: 1rem;
}

.login-header h1 {
  color: var(--text-primary);
  font-size: clamp(2rem, 5vw, 3rem);
  line-height: 1.04;
  margin-bottom: 0.6rem;
}

.login-header p {
  color: var(--text-secondary);
}

.eyebrow {
  color: var(--accent);
  font-size: 0.78rem;
  font-weight: 800;
  letter-spacing: 0;
  text-transform: uppercase;
}

.login-card {
  background: var(--bg-card);
  border: 1px solid var(--border);
  border-radius: 10px;
  box-shadow: var(--shadow-md);
  padding: clamp(1.25rem, 3vw, 1.75rem);
}

.form-group {
  margin-bottom: 1rem;
}

.form-input {
  min-height: 44px;
}

.form-input.error {
  border-color: rgba(252, 165, 165, 0.82);
}

.phone-input-wrap {
  min-height: 44px;
  display: flex;
  align-items: center;
  border: 1px solid var(--border);
  border-radius: 10px;
  background: #fff;
  overflow: hidden;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.phone-input-wrap:focus-within {
  border-color: var(--accent);
  box-shadow: 0 0 0 3px rgba(91, 124, 255, 0.14);
}

.phone-input-wrap.error {
  border-color: rgba(252, 165, 165, 0.82);
}

.country-prefix {
  flex: 0 0 auto;
  color: var(--accent);
  font-weight: 800;
  padding: 0 0.85rem;
  border-right: 1px solid var(--border);
}

.phone-input {
  width: 100%;
  border: 0;
  outline: 0;
  padding: 0.75rem 0.9rem;
  color: var(--text-primary);
  font: inherit;
  background: transparent;
}

.form-help {
  margin-top: 0.4rem;
  color: var(--text-muted);
  font-size: 0.84rem;
}

.error-message {
  display: block;
  margin-top: 0.45rem;
  color: #b91c1c;
  font-size: 0.84rem;
  font-weight: 700;
}

.login-footer {
  margin: 1rem 0 0;
  color: var(--text-muted);
  font-size: 0.86rem;
  text-align: center;
}

.link {
  color: var(--text-primary);
  font-weight: 800;
  text-decoration: none;
}

.link:hover {
  color: var(--accent);
}

.code-header {
  text-align: center;
  margin-bottom: 1rem;
}

.code-header h2 {
  color: var(--text-primary);
  font-size: 1rem;
  margin-bottom: 0.35rem;
}

.code-input {
  text-align: center;
  font-size: 1.2rem;
  font-weight: 800;
  letter-spacing: 0.12em;
}

.login-actions {
  display: flex;
  gap: 0.7rem;
  flex-wrap: wrap;
}

.login-actions .btn {
  flex: 1 1 140px;
}

.btn {
  min-height: 44px;
}

.spinner {
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid #fff;
  border-radius: 50%;
  width: 16px;
  height: 16px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0); }
  100% { transform: rotate(360deg); }
}
</style>
